import { Form, Formik, FormikHelpers } from "formik"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import React from "react"
import { IClientBonCommandeModel, IClientPropalModel, IHttpErrorResponseModel } from "@common-models/*"
import { validationPropalParClientRequest } from "../core/_requests"
import { AxiosError } from "axios"
import { CONST_CLIENT_BON_COMMANDE_TYPE_BC_ORIGINAL, CONST_CLIENT_BON_COMMANDE_TYPE_MAIL_CONFIRMATION, CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR } from "@common-constants/*"
import { toast } from "react-toastify"
import MyAlert from "@common-utils/MyAlert"
import { IValidationPtfParClientRequestModel } from "../core/_models"
import MySelectField from "@common-utils/fields/MySelectField"
import MyDateDayMonthYearField from "@common-utils/fields/MyDateDayMonthYearField"
import MyFileField from "@common-utils/fields/MyFileField"

const PtfValidationAffaireForm = ({ ptf, onSubmitCallBack = () => {} }: IPtfValidationAffaireFormPropsModel) => {
     const initialValues: IValidationPtfParClientRequestModel = {}

     function handleSubmit(values: IValidationPtfParClientRequestModel, helpers: FormikHelpers<IValidationPtfParClientRequestModel>) {
          helpers.setStatus(null)

          validationPropalParClientRequest(ptf.id as number, values)
               .then(r => {
                    toast.success("L'affaire a bien été validée")
                    onSubmitCallBack(r.data)
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data

                    // Set form errors
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                    }

                    // Set form global status and notify user using a toast
                    helpers.setStatus(error?.detail)
                    toast.error(error?.detail, { autoClose: false })

                    // Stop submit loader
                    helpers.setSubmitting(false)
               })
     }

     return (
          <>
               <Formik initialValues={initialValues} onSubmit={handleSubmit}>
                    {helpers => {
                         return (
                              <Form noValidate autoComplete="off">
                                   {helpers.status && (
                                        <MyAlert type={"danger"} classNames={"mb-4"}>
                                             {helpers.status}
                                        </MyAlert>
                                   )}

                                   <MyAlert type={"success"} classNames={"mb-4"}>
                                        <>Veuillez joindre votre bon de commande ou un accord client pour valider cette affaire.</>
                                   </MyAlert>

                                   {/* Type */}
                                   <div className="row mb-4">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Type</label>

                                        <div className="col-lg-8">
                                             <MySelectField
                                                  name={"bonCommandeType"}
                                                  options={[
                                                       {
                                                            value: CONST_CLIENT_BON_COMMANDE_TYPE_MAIL_CONFIRMATION,
                                                            label: "Mail de confirmation / accord client",
                                                       },
                                                       {
                                                            value: CONST_CLIENT_BON_COMMANDE_TYPE_BC_ORIGINAL,
                                                            label: "Bon de commande original",
                                                       },
                                                  ]}
                                                  value={helpers.values.bonCommandeType}
                                                  isInvalid={!!helpers.errors.bonCommandeType}
                                                  onChange={val => helpers.setFieldValue("bonCommandeType", val)}
                                             />
                                             {helpers.errors.bonCommandeType && <div className={"text-danger"}>{helpers.errors.bonCommandeType}</div>}
                                        </div>
                                   </div>

                                   {/* Période */}
                                   <div className="row mb-4">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Période</label>

                                        <div className="col-lg-8">
                                             <div className="d-flex">
                                                  <MyDateDayMonthYearField
                                                       name={"bonCommandeDateDebut"}
                                                       value={helpers.values.bonCommandeDateDebut}
                                                       isInvalid={!!helpers.errors.bonCommandeDateDebut}
                                                       maxDate={helpers.values.bonCommandeDateFin}
                                                       onChange={val => helpers.setFieldValue("bonCommandeDateDebut", val)}
                                                       placeholder={"Date de début"}
                                                       classNames={"rounded-0 rounded-start"}
                                                  />
                                                  <span className="input-group-text fas fa-ellipsis d-flex align-items-center rounded-0" />
                                                  <MyDateDayMonthYearField
                                                       name={"bonCommandeDateFin"}
                                                       value={helpers.values.bonCommandeDateFin}
                                                       minDate={helpers.values.bonCommandeDateDebut}
                                                       isInvalid={!!helpers.errors.bonCommandeDateFin}
                                                       onChange={val => helpers.setFieldValue("bonCommandeDateFin", val)}
                                                       placeholder={"Date de fin"}
                                                       classNames={"rounded-0 rounded-end"}
                                                  />
                                             </div>
                                             {helpers.errors.bonCommandeDateDebut && <div className={"text-danger"}>{helpers.errors.bonCommandeDateDebut}</div>}
                                             {helpers.errors.bonCommandeDateFin && <div className={"text-danger"}>{helpers.errors.bonCommandeDateFin}</div>}
                                        </div>
                                   </div>

                                   {/* Document BC */}
                                   <div className="row mb-4">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">BC / Accord client</label>
                                        <div className="col-lg-8">
                                             <MyFileField
                                                  name={"bonCommandeDocument"}
                                                  isInvalid={!!helpers.errors.bonCommandeDocument}
                                                  onChange={val => helpers.setFieldValue("bonCommandeDocument", val)}
                                             />
                                             {helpers.errors.bonCommandeDocument && <div className={"text-danger"}>{helpers.errors.bonCommandeDocument}</div>}
                                        </div>
                                   </div>

                                   {/* Submit button */}
                                   <div className={"d-flex justify-content-end"}>
                                        <button className={"btn btn-sm btn-primary d-inline-flex align-items-center"} type={"submit"} disabled={helpers.isSubmitting}>
                                             {!helpers.isSubmitting ? <>Valider</> : <MySimpleSpinner size={"sm"} />}
                                        </button>
                                   </div>
                              </Form>
                         )
                    }}
               </Formik>
          </>
     )
}

interface IPtfValidationAffaireFormPropsModel {
     ptf: IClientPropalModel
     onSubmitCallBack?: (val: IClientBonCommandeModel) => void
}

export default PtfValidationAffaireForm
